import { Box, Typography } from '@mui/material';

import { OPACITY } from '../../../theme';
import { histogramOf } from '../growth';
import { GROWTH } from '../tokens';
import { SKILL_LABELS, type SchemeLevel, type Skill } from '../types';
import { BandDot, CardHead, GROWTH_CARD_SX, SMALL_SX, bandNames, colorOf } from './cardParts';

const BAR_HEIGHT = 140;
const BAR_AREA = 168;

export function BandHistogram({ grid, scheme, skill, later, sub, open }: { grid: number[][]; scheme: SchemeLevel[]; skill: Skill; later: string; sub: string; open: boolean }) {
  const names = bandNames(scheme);
  const h = histogramOf(grid);
  const max = Math.max(1, ...h.boy, ...h.later);
  const height = (value: number) => `${Math.max(2, Math.round((value / max) * BAR_HEIGHT))}px`;
  const description = names.map((name, i) => `${name}: ${h.boy[i] ?? 0}% at BOY, ${h.later[i] ?? 0}% at ${later}`).join('; ');
  const faded = { opacity: open ? OPACITY.provisional : 1 };
  const pivot = names[2] || 'Expanding';
  return (
    <Box component="section" data-testid="band-histogram" sx={GROWTH_CARD_SX}>
      <CardHead title={`${SKILL_LABELS[skill]} levels at BOY and ${later}`} sub={sub} open={open} />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '32px', pb: '24px' }}>
        <Box data-testid="histogram-below" sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }} style={faded}>
          <Typography component="small" sx={SMALL_SX}>
            {`Below ${pivot}`}
          </Typography>
          <Typography component="b" sx={{ fontFamily: GROWTH.font, fontSize: '12px', lineHeight: '16px', fontWeight: 700, color: GROWTH.ink, fontVariantNumeric: 'tabular-nums' }}>
            {`${h.belowBoy}% → ${h.belowLater}%`}
          </Typography>
        </Box>
        <Box data-testid="histogram-above" sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }} style={faded}>
          <Typography component="small" sx={SMALL_SX}>
            {`${pivot} or above`}
          </Typography>
          <Typography component="b" sx={{ fontFamily: GROWTH.font, fontSize: '12px', lineHeight: '16px', fontWeight: 700, color: GROWTH.ink, fontVariantNumeric: 'tabular-nums' }}>
            {`${h.aboveBoy}% → ${h.aboveLater}%`}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center', pb: '24px' }}>
        <Typography component="span" sx={{ ...SMALL_SX, display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <Box component="i" aria-hidden="true" data-testid="histogram-boy-swatch" sx={{ width: 12, height: 12, borderRadius: '4px', backgroundImage: GROWTH.hatch }} />
          BOY
        </Typography>
        <Typography component="span" sx={{ ...SMALL_SX, display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <Box component="i" aria-hidden="true" sx={{ width: 12, height: 12, borderRadius: '4px', bgcolor: GROWTH.disabled }} />
          {later}
        </Typography>
      </Box>
      <Box role="img" aria-label={description} sx={{ display: 'flex', gap: '32px', alignItems: 'flex-end', position: 'relative', width: 'fit-content' }}>
        <Box aria-hidden="true" data-testid="histogram-baseline" sx={{ position: 'absolute', left: 0, right: 0, top: BAR_AREA, height: 2, bgcolor: GROWTH.ink }} />
        {names.map((name, i) => (
          <Box key={name} data-testid="histogram-band" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-end', height: BAR_AREA }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: '4px', height: '100%', ...SMALL_SX, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                <span>{`${h.boy[i] ?? 0}%`}</span>
                <Box component="i" data-testid="histogram-boy-bar" sx={{ display: 'block', width: 40, borderRadius: '4px 4px 0 0', backgroundImage: GROWTH.hatch }} style={{ height: height(h.boy[i] ?? 0) }} />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: '4px', height: '100%', ...SMALL_SX, fontWeight: 700, color: GROWTH.ink, fontVariantNumeric: 'tabular-nums' }}>
                <span data-testid="histogram-later-percent" style={faded}>{`${h.later[i] ?? 0}%`}</span>
                <Box component="i" data-testid="histogram-later-bar" sx={{ display: 'block', width: 40, borderRadius: '4px 4px 0 0', bgcolor: colorOf(scheme, i + 1) }} style={{ height: height(h.later[i] ?? 0), opacity: open ? OPACITY.provisional : 1 }} />
              </Box>
            </Box>
            <Typography component="span" sx={{ ...SMALL_SX, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <BandDot scheme={scheme} band={i + 1} />
              {name}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
